"use client";

import React, { useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from "chart.js";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useDposPerformance } from "../../hooks/query/dposQueries";
import { formatRandsDisplay } from "../../utils/zarDisplayFormat";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

const TIME_FILTERS = [
  { id: "1d", label: "1D" },
  { id: "1w", label: "1W" },
  { id: "1m", label: "1M" },
  { id: "1y", label: "1Y" },
  { id: "all", label: "All" },
];

const CHART_FONT_FAMILY = "PlusJakartaSans, sans-serif";

function formatYTick(value: number): string {
  if (value < 0) return `${value}`;
  if (value >= 1000) {
    const k = value / 1000;
    return `${k.toFixed(1)}k`;
  }
  return `${value}`;
}

function formatAmount(value: number): string {
  return formatRandsDisplay(value);
}

function formatLabel(label: string, period: string): string {
  if (!label) return label;
  try {
    if (period === "1d") {
      // "2024-05-10T14:00:00Z" → "14:00"
      const d = new Date(label);
      return d.toLocaleTimeString("en-ZA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } else if (period === "1w" || period === "1m") {
      // "2024-05-10" → "10 May" or "Fri"
      const d = new Date(label + "T00:00:00Z");
      return period === "1w"
        ? d.toLocaleDateString("en-ZA", { weekday: "short", timeZone: "UTC" })
        : d.toLocaleDateString("en-ZA", {
            day: "numeric",
            month: "short",
            timeZone: "UTC",
          });
    } else {
      // "2024-05" → "May '24"
      const d = new Date(label + "-01T00:00:00Z");
      return d.toLocaleDateString("en-ZA", {
        month: "short",
        year: "2-digit",
        timeZone: "UTC",
      });
    }
  } catch {
    return label;
  }
}

interface DPOSPerformanceChartProps {
  businessId: string;
  isPageLoading?: boolean;
}

export default function DPOSPerformanceChart({
  businessId,
  isPageLoading = false,
}: DPOSPerformanceChartProps) {
  const [activeFilter, setActiveFilter] = useState("1w");

  const { data: performance, isLoading: isPerfLoading } = useDposPerformance(
    businessId || null,
    activeFilter
  );

  const isLoading = isPageLoading || (isPerfLoading && !performance);

  const displayLabels = useMemo(() => {
    if (!performance?.labels?.length) return [];
    return performance.labels.map((l: string) => formatLabel(l, activeFilter));
  }, [performance, activeFilter]);

  const displayValues = useMemo(() => {
    if (!performance?.values?.length) return [];
    return performance.values as number[];
  }, [performance]);

  const chartData = useMemo(
    () => ({
      labels: displayLabels,
      datasets: [
        {
          label: "Performance",
          data: displayValues,
          fill: true,
          borderColor: "#8B5CF6",
          backgroundColor: (context: any) => {
            const { chart } = context;
            const { ctx, chartArea } = chart;
            if (!chartArea) return "rgba(139,92,246,0.15)";
            const gradient = ctx.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom
            );
            gradient.addColorStop(0, "rgba(139,92,246,0.30)");
            gradient.addColorStop(1, "rgba(139,92,246,0.0)");
            return gradient;
          },
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ],
    }),
    [displayLabels, displayValues]
  );

  const yMax = useMemo(() => {
    if (!displayValues.length) return 1000;
    const max = Math.max(...displayValues);
    return Math.ceil((max * 1.3) / 500) * 500 || 1000;
  }, [displayValues]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "rgba(30,20,50,0.92)",
          titleColor: "#fff",
          bodyColor: "#fff",
          borderColor: "rgba(255,255,255,0.2)",
          borderWidth: 1,
          padding: 10,
          displayColors: false,
          callbacks: {
            label: (ctx: any) => {
              const y = Number(ctx.parsed.y);
              return Number.isFinite(y) ? formatRandsDisplay(y) : "";
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            color: "rgba(0,0,0,0.06)",
            drawTicks: false,
          },
          border: { display: false },
          ticks: {
            color: (ctx: any) =>
              ctx.index === displayLabels.length - 1 ? "#33125F" : "#9CA3AF",
            font: (ctx: any) => ({
              family: CHART_FONT_FAMILY,
              size: 11,
              weight: ctx.index === displayLabels.length - 1 ? "700" : "400",
            }),
          },
        },
        y: {
          min: 0,
          max: yMax,
          grid: {
            display: true,
            color: "rgba(0,0,0,0.06)",
            drawTicks: false,
          },
          border: { display: false },
          ticks: {
            color: "#9CA3AF",
            font: { family: CHART_FONT_FAMILY, size: 11 },
            callback: (value: any) => formatYTick(value),
            padding: 8,
          },
        },
      },
    }),
    [displayLabels, yMax]
  );

  const totalAmount = performance?.totalAmount ?? 0;
  const allTimeTotal = performance?.allTimeTotal ?? 0;
  const change = performance?.change ?? 0;
  const changePercent = performance?.changePercent ?? null;
  const isPositive = change >= 0;

  return (
    <div className="bg-white border border-[#EBE9EF] rounded-2xl p-6 flex flex-col h-full">
      {/* Time filter tabs */}
      <div className="flex gap-2 mb-5">
        {TIME_FILTERS.map((f) => {
          const isActive = activeFilter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setActiveFilter(f.id)}
              className={`rounded-full px-5 h-[44px] text-[14px] font-semibold transition-colors ${
                isActive
                  ? "bg-[#33125F] text-white"
                  : "bg-transparent text-[#6D6584] border border-[#EBE9EF] hover:bg-[#F5F4F7]"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Amount */}
      <div className="mb-4">
        {isLoading ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-8 w-40 bg-gray-200 rounded" />
            <div className="h-4 w-56 bg-gray-200 rounded" />
          </div>
        ) : (
          <>
            <p className="text-[32px] font-bold text-[#33125F] leading-none">
              {formatAmount(totalAmount)}
            </p>
            {changePercent !== null && (
              <p className="text-[13px] mt-2 flex items-center gap-1 flex-wrap">
                <span
                  className={`font-semibold ${isPositive ? "text-[#17C3A5]" : "text-[#EB3366]"}`}
                >
                  {isPositive ? "+" : ""}
                  {formatAmount(change)} ({isPositive ? "+" : ""}
                  {changePercent}%)
                </span>
                <span className="text-[#6D6584]">vs previous period</span>
              </p>
            )}
          </>
        )}
      </div>

      {/* Chart — grows to fill remaining card height */}
      <div className="relative flex-1 min-h-[180px]">
        {isLoading ? (
          <div className="absolute inset-0 bg-gray-50 rounded-xl animate-pulse" />
        ) : displayValues.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-[13px] text-[#9CA3AF]">
            No data for this period
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>

      {/* Footer note */}
      <p className="text-[11px] text-[#9CA3AF] mt-3 leading-relaxed">
        *Includes all paid DPOS sessions in the selected time frame.
      </p>

      {/* All time performance */}
      <div className="mt-4 bg-[#F5F4F7] rounded-full py-3 px-5 flex items-center justify-center gap-2">
        <span className="text-[13px] text-[#6D6584]">All time revenue:</span>
        {allTimeTotal >= 0 ? (
          <TrendingUp className="w-4 h-4 text-[#17C3A5]" />
        ) : (
          <TrendingDown className="w-4 h-4 text-[#EB3366]" />
        )}
        <span className="text-[13px] font-semibold text-[#17C3A5]">
          {formatAmount(allTimeTotal)}
        </span>
      </div>
    </div>
  );
}
